// Only explicit customizing opts in; citizen forms retain their existing tooltips.
export function resolveHelpUrl(value, baseUrl) {
  if (typeof value !== 'string' || !value.trim()) return null;
  try {
    const url = new URL(value.trim(), baseUrl);
    return ['http:', 'https:'].includes(url.protocol) ? url.href : null;
  } catch (_) { return null; }
}

export function createComponentHelpHooks({ context, entries = [], baseUrl } = {}) {
  if (context !== 'customizing') return {};
  const globalHelp = new Map(entries.map(entry => [entry.componentType, entry]));
  const attached = new WeakMap();
  let sequence = 0;
  let closeActive = null;
  const attachComponent = (element, instance) => {
    const old = attached.get(instance);
    if (old) old();
    attached.delete(instance);
    const c = instance.component;
    if (!c || instance.builderMode || instance.previewMode || ['hidden', 'button', 'unknown'].includes(c.type)) return;
    const global = globalHelp.get(c.type) || {};
    const sections = [
      { title: 'Zur Komponente', text: global.explanationText, url: global.documentationUrl },
      { title: 'In diesem Formular', text: c.documentationText, url: c.documentation }
    ].map(s => ({ ...s, text: typeof s.text === 'string' ? s.text.trim() : '', href: resolveHelpUrl(s.url, baseUrl) }))
      .filter(s => s.text || s.href);
    if (!sections.length) return;
    const doc = element.ownerDocument;
    const wrapper = doc.createElement('span');
    wrapper.className = 'ozg-component-help';
    wrapper.style.cssText = 'display:inline-block;position:relative;margin:.25rem;vertical-align:middle;';
    wrapper.setAttribute('data-noattach', 'true');
    const trigger = doc.createElement('button');
    trigger.type = 'button';
    trigger.textContent = '?';
    trigger.style.cssText = 'border:1px solid currentColor;border-radius:50%;background:white;color:#175582;width:1.5rem;height:1.5rem;line-height:1;padding:0;cursor:pointer;';
    trigger.setAttribute('aria-label', `Hilfe: ${c.title || c.label || c.key || c.type}`);
    trigger.setAttribute('aria-expanded', 'false');
    const panel = doc.createElement('span');
    panel.id = `ozg-help-${instance.id || 'component'}-${++sequence}`;
    panel.setAttribute('role', 'region');
    panel.setAttribute('aria-label', 'Komponentenhilfe');
    panel.className = 'ozg-component-help-panel';
    panel.style.cssText = 'position:fixed;z-index:2147483647;inset:auto;margin:0;box-sizing:border-box;width:320px;max-width:calc(100vw - 16px);overflow:auto;overscroll-behavior:contain;padding:1rem;background:white;color:#212529;border:1px solid #aaa;border-radius:.3rem;box-shadow:0 3px 10px #0003;text-align:left;white-space:normal;font-size:14px;line-height:1.5;font-weight:normal;';
    panel.hidden = true;
    trigger.setAttribute('aria-controls', panel.id);
    sections.forEach(section => {
      const block = doc.createElement('span');
      block.style.cssText = 'display:block;margin-bottom:.5rem;';
      const title = doc.createElement('strong');
      title.style.display = 'block';
      title.textContent = section.title;
      block.appendChild(title);
      if (section.text) {
        const text = doc.createElement('span');
        text.style.cssText = 'display:block;white-space:pre-wrap;overflow-wrap:anywhere;';
        text.textContent = section.text;
        block.appendChild(text);
      }
      if (section.href) {
        const link = doc.createElement('a');
        link.href = section.href;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.textContent = 'Dokumentation öffnen';
        block.appendChild(link);
      }
      panel.appendChild(block);
    });
    const win = doc.defaultView;
    let nativePopover = typeof panel.showPopover === 'function';
    if (nativePopover) panel.setAttribute('popover', 'manual');
    let opened = false;
    let pinned = false;
    let pointerInside = false;
    let closeTimer;
    let observer;
    const contains = node => !!node && (wrapper.contains(node) || panel.contains(node));
    const cancelClose = () => { if (closeTimer !== undefined) win.clearTimeout(closeTimer); closeTimer = undefined; };
    const position = () => {
      if (!opened) return;
      if (!trigger.isConnected || !trigger.getClientRects().length) { hide(); return; }
      const viewport = win.visualViewport;
      const left = viewport ? viewport.offsetLeft : 0;
      const top = viewport ? viewport.offsetTop : 0;
      const width = viewport ? viewport.width : doc.documentElement.clientWidth;
      const height = viewport ? viewport.height : win.innerHeight;
      const margin = 8, gap = 4;
      const anchor = trigger.getBoundingClientRect();
      // A scrolled-out trigger must not leave an unrelated floating help window.
      if (anchor.bottom < top || anchor.top > top + height || anchor.right < left || anchor.left > left + width) { hide(); return; }
      panel.style.width = `${Math.min(320, Math.max(0, width - 2 * margin))}px`;
      panel.style.maxHeight = `${Math.max(0, height - 2 * margin)}px`;
      const needed = panel.getBoundingClientRect().height;
      const below = Math.max(0, top + height - margin - anchor.bottom - gap);
      const above = Math.max(0, anchor.top - top - margin - gap);
      const useAbove = needed > below && above > below;
      panel.style.maxHeight = `${useAbove ? above : below}px`;
      const bounds = panel.getBoundingClientRect();
      panel.style.left = `${Math.max(left + margin, Math.min(anchor.left, left + width - bounds.width - margin))}px`;
      panel.style.top = `${Math.max(top + margin, useAbove ? anchor.top - gap - bounds.height : anchor.bottom + gap)}px`;
    };
    const outside = event => { if (!contains(event.target)) hide(); };
    const onKey = event => {
      if (event.key === 'Enter' || event.key === ' ') event.stopPropagation();
      if (event.key === 'Escape') {
        event.stopPropagation();
        if (opened) { event.preventDefault(); trigger.focus(); hide(); }
      }
    };
    const escape = event => { if (event.key === 'Escape') onKey(event); };
    const hide = () => {
      cancelClose();
      if (!opened) return;
      opened = false;
      pinned = false;
      pointerInside = false;
      if (nativePopover && panel.matches(':popover-open')) panel.hidePopover();
      panel.hidden = true;
      // The fallback portal lives only while open, so removed rows cannot leak it.
      if (!nativePopover) wrapper.appendChild(panel);
      trigger.setAttribute('aria-expanded', 'false');
      win.removeEventListener('scroll', position, true);
      win.removeEventListener('resize', position);
      win.visualViewport?.removeEventListener('resize', position);
      win.visualViewport?.removeEventListener('scroll', position);
      doc.removeEventListener('pointerdown', outside, true);
      doc.removeEventListener('keydown', escape, true);
      observer?.disconnect();
      observer = undefined;
      if (closeActive === hide) closeActive = null;
    };
    const show = () => {
      cancelClose();
      if (opened || !trigger.isConnected) return;
      if (closeActive) closeActive();
      closeActive = hide;
      opened = true;
      panel.hidden = false;
      // The native top layer escapes table overflow, transformed containers and
      // stacking contexts, including forms inside dialogs. Keep DOM tab order.
      if (nativePopover) {
        try { panel.showPopover(); }
        catch (_) { nativePopover = false; panel.removeAttribute('popover'); }
      }
      if (!nativePopover) (trigger.closest('dialog[open]') || doc.body).appendChild(panel);
      trigger.setAttribute('aria-expanded', 'true');
      win.addEventListener('scroll', position, true);
      win.addEventListener('resize', position);
      win.visualViewport?.addEventListener('resize', position);
      win.visualViewport?.addEventListener('scroll', position);
      doc.addEventListener('pointerdown', outside, true);
      doc.addEventListener('keydown', escape, true);
      observer = new win.MutationObserver(() => {
        if (!trigger.isConnected || !trigger.getClientRects().length) hide();
      });
      observer.observe(doc.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'hidden', 'open'] });
      position();
    };
    const enter = () => { pointerInside = true; show(); };
    const leave = (event = {}) => {
      if (contains(event.relatedTarget)) return;
      pointerInside = false;
      cancelClose();
      // Keep the link reachable across the small gap between icon and panel.
      closeTimer = win.setTimeout(() => {
        closeTimer = undefined;
        if (!pinned && !pointerInside && !contains(doc.activeElement)) hide();
      }, 180);
    };
    const click = event => {
      event.stopPropagation();
      if (!event.target.closest('a')) event.preventDefault();
    };
    [wrapper, panel].forEach(host => {
      host.addEventListener('mouseenter', enter);
      host.addEventListener('mouseleave', leave);
      host.addEventListener('focusin', show);
      host.addEventListener('focusout', event => { if (!contains(event.relatedTarget)) hide(); });
      host.addEventListener('click', click);
      host.addEventListener('keydown', onKey);
    });
    trigger.addEventListener('click', event => {
      event.preventDefault(); event.stopPropagation();
      if (pinned) hide(); else { show(); pinned = true; }
    });
    wrapper.appendChild(trigger);
    wrapper.appendChild(panel);
    // Match only this panel's own header, never a nested panel's title.
    let helpHost;
    if (c.type === 'panel') {
      helpHost = element.querySelector(':scope > .card > .card-header > .card-title');
    } else if (c.type === 'fieldset') {
      helpHost = element.querySelector(':scope > fieldset > legend');
    } else if (c.type === 'checkbox') {
      // Checkbox tooltips are siblings of the input label in Form.io.
      helpHost = element.querySelector(':scope > .form-check');
    } else {
      const labelId = `l-${instance.id}-${c.key}`;
      helpHost = Array.from(element.querySelectorAll('label[ref="label"]'))
        .find(label => label.id === labelId);
    }
    (helpHost || element).appendChild(wrapper);
    // Form.io detach/destroy may leave the DOM in place. Dispose overlays at
    // that lifecycle boundary as well as on DOM removal and reattachment.
    const originalDetach = instance.detach;
    const dispose = () => {
      hide();
      wrapper.remove();
      attached.delete(instance);
      if (instance.detach === detachWithHelp) instance.detach = originalDetach;
    };
    function detachWithHelp(...args) {
      dispose();
      return originalDetach.apply(this, args);
    }
    if (typeof originalDetach === 'function') instance.detach = detachWithHelp;
    attached.set(instance, dispose);
  };
  return { attachComponent, attachWebform: (element, form) => { if (form.pages) attachComponent(element, form); } };
}
